import {
  AIPill,
  NavLinkData,
  type SerializedVehicleReport,
} from '@ltvco/refresh-lib/v1';
import { ReportMenuItem } from '@ltvco/refresh-lib/v2';
import {
  Attachment,
  ConstructionOutlined,
  DirectionsCarOutlined,
  FeedOutlined,
  Groups,
  HistoryOutlined,
  InfoOutlined,
  Lock,
  StorefrontOutlined,
  SummarizeOutlined,
} from '@mui/icons-material';

export const getVehicleNavLinkDataV2 = (
  reportData: SerializedVehicleReport | null,
  _complaintsCount: number,
  theme?: any
) => {
  // AB Tests

  const automobile = reportData?.automobiles[0];

  if (!reportData) {
    return [];
  }

  const isYMMSearch = reportData.meta?.search_by === 'ymm';

  const ymmPresent = [
    automobile?.year,
    automobile?.make,
    automobile?.model,
  ].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const shouldShowSmartInsightsNavLink = !isYMMSearch && hasAllYMMPresent;

  const navLinkData: ReportMenuItem[] = [
    {
      href: '#vehicle-overview',
      sectionId: '#vehicle-overview',
      labelText: 'Photos',
      icons: [
        <DirectionsCarOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '2.5rem',
          }}
        />,
      ],
      reportItemsCount: automobile?.imagesCount || null,
      hideReportItemsCount: false,
      notificationSection: 'identity',
      visible: true,
    },
    {
      labelText: 'Overview',
      name: 'overview',
      icons: [
        <SummarizeOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '1.4rem',
          }}
        />,
      ],
      visible: true,
      childItems: [
        {
          href: '#vehicle-overview',
          sectionId: '#vehicle-overview',
          labelText: 'Photos',
          name: 'vehicle-photos',
          reportItemsCount: automobile?.imagesCount || null,
          hideReportItemsCount: false,
          visible: true,
          notificationSection: 'identity',
        },
        {
          href: '#smart-insights',
          sectionId: '#smart-insights',
          labelText: 'Smart Insights',
          name: 'vehicle-smart-insights',
          visible: shouldShowSmartInsightsNavLink,
          reportItemsCount: 0,
          notificationSection: 'smart insights',
          icons: [<AIPill />],
          showIcon: true,
        },
      ],
    },
    {
      labelText: 'Vehicle Background',
      name: 'vehicle-history',
      icons: [
        <HistoryOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '1.4rem',
          }}
        />,
      ],
      visible: !isYMMSearch,
      childItems: [
        {
          href: '#vehicle-ownership-history',
          sectionId: 'vehicle-ownership-history',
          labelText: 'Ownership History',
          name: 'vehicle-ownership-history',
          reportItemsCount: Math.max(
            (automobile?.ownersByTitleRecordsCount as number) || 0,
            automobile?.ownershipHistoryCount || 0
          ),
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-title-records',
          sectionId: 'vehicle-title-records',
          labelText: 'Title Records',
          name: 'vehicle-title-records',
          reportItemsCount: automobile?.titleRegistrationsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          icons: [
            <Lock
              fontSize="small"
              sx={{
                marginLeft: '10px',
                color: theme?.palette?.text?.secondary || '#212121',
              }}
            />,
          ],
          showIcon:
            !automobile?.nmvtis_success &&
            automobile?.titleRegistrationsCount === 0,
        },
        {
          href: '#vehicle-title-brands',
          sectionId: '#vehicle-title-brands',
          labelText: 'Title Brands',
          name: 'vehicle-title-brands',
          reportItemsCount: automobile?.titleChecksCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          icons: [
            <Lock
              fontSize="small"
              sx={{
                marginLeft: '10px',
                color: theme?.palette?.text?.secondary || '#212121',
              }}
            />,
          ],
          showIcon:
            !automobile?.nmvtis_success && automobile?.titleChecksCount === 0,
        },
        {
          href: '#vehicle-liens',
          sectionId: 'vehicle-liens',
          labelText: 'Liens',
          name: 'vehicle-liens',
          reportItemsCount: automobile?.liensCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-impounds',
          sectionId: '#vehicle-impounds',
          labelText: `Impounds & Exports`,
          name: 'vehicle-impounds-exports',
          reportItemsCount:
            (automobile?.impoundsCount || 0) + (automobile?.exportsCount || 0),
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-thefts',
          sectionId: '#vehicle-thefts',
          labelText: 'Theft Records',
          name: 'vehicle-thefts',
          reportItemsCount: automobile?.theftsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: 'vehicle-inspections-section',
          sectionId: 'vehicle-inspections-section',
          name: 'vehicle-inspections',
          labelText: 'Inspection Records',
          reportItemsCount: automobile?.inspectionEventsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-sales',
          sectionId: '#vehicle-sales',
          labelText: 'Sale Listings',
          name: 'vehicle-sales',
          reportItemsCount: automobile?.listingsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-auctions',
          sectionId: '#vehicle-auctions',
          labelText: 'Auctions',
          name: 'vehicle-auctions',
          reportItemsCount: automobile?.auctionsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#detailed-history',
          sectionId: '#detailed-history',
          name: 'vehicle-detailed-history',
          labelText: 'Detailed History',
          reportItemsCount: automobile?.detailedHistoryCount,
          hideReportItemsCount: false,
          visible: Boolean(!isYMMSearch && automobile?.detailedHistoryCount),
          notificationSection: 'history',
        },
      ],
    },
    {
      labelText: 'Damages & Repairs',
      name: 'damages-repairs',
      icons: [
        <ConstructionOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '1.4rem',
          }}
        />,
      ],
      visible: true,
      childItems: [
        {
          href: '#vehicle-accidents',
          sectionId: '#vehicle-accidents',
          labelText: 'Accidents',
          name: 'vehicle-accidents',
          reportItemsCount: automobile?.accidents?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-damages',
          sectionId: '#vehicle-damages',
          labelText: 'Damages',
          name: 'vehicle-damages',
          reportItemsCount:
            automobile?.damage_history?.reported_damage?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-repair-quotes',
          sectionId: '#vehicle-repair-quotes',
          labelText: 'Repair Quotes',
          name: 'vehicle-repair-quotes',
          reportItemsCount:
            automobile?.damage_history?.repair_quotes?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-salvages',
          sectionId: '#vehicle-salvages',
          labelText: 'Salvages',
          name: 'vehicle-salvages',
          reportItemsCount: automobile?.salvage?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          icons: [
            <Lock
              key="lock-icon-salvages"
              fontSize="small"
              sx={{
                marginLeft: '10px',
                color: 'text.primary',
              }}
            />,
          ],
          showIcon:
            !automobile?.nmvtis_success &&
            (automobile?.salvage?.length === 0 || !automobile?.salvage),
        },
        {
          href: '#vehicle-service-and-repairs',
          sectionId: '#vehicle-service-and-repairs',
          name: 'vehicle-service-and-repairs',
          labelText: 'Service History',
          reportItemsCount: automobile?.repairsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          notificationSection: 'maintenance',
        },
        {
          href: '#vehicle-maintenance-schedule',
          sectionId: '#vehicle-maintenance-schedule',
          name: 'vehicle-maintenance-schedule',
          labelText: 'Maintenance Schedule',
          reportItemsCount: automobile?.maintenanceCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
        },
        {
          href: '#vehicle-recalls',
          sectionId: '#vehicle-recalls',
          name: 'vehicle-recalls',
          labelText: 'Recalls',
          reportItemsCount: isYMMSearch
            ? automobile?.recallsCount
            : automobile?.recallsWithStatusCount,
          hideReportItemsCount: false,
          visible: true,
          notificationSection: 'prices',
        },
      ],
    },

    {
      labelText: 'Market & Value',
      name: 'market-value',
      icons: [
        <StorefrontOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '1.4rem',
          }}
        />,
      ],
      visible: true,
      childItems: [
        {
          href: '#vehicle-market',
          sectionId: '#vehicle-market',
          labelText: 'Market Values',
          name: 'vehicle-market',
          hideReportItemsCount: true,
          visible: true,
        },
        {
          href: '#comparable-vehicles',
          sectionId: '#comparable-vehicles',
          labelText: 'Comps for Sale',
          name: 'vehicle-comps-for-sale',
          hideReportItemsCount: true,
          visible: true,
        },
        {
          href: '#vehicle-ownership-cost',
          sectionId: 'vehicle-ownership-cost',
          labelText: 'Ownership Costs',
          name: 'vehicle-ownership-cost',
          hideReportItemsCount: true,
          visible: true,
        },
      ],
    },

    {
      labelText: 'Features & Specs',
      name: 'features-specs',
      icons: [
        <FeedOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '1.4rem',
          }}
        />,
      ],
      visible: true,
      childItems: [
        {
          href: '#vehicle-specs',
          sectionId: '#vehicle-specs',
          name: 'vehicle-specs',
          labelText: 'Specs',
          hideReportItemsCount: true,
          notificationSection: 'specifications',
          visible: true,
        },
        {
          href: '#installed-options-packages',
          sectionId: '#installed-options-packages',
          name: 'vehicle-options-packages',
          labelText: 'Options & Packages',
          hideReportItemsCount: true,
          visible: true,
        },
        {
          href: '#equipment-specifications',
          sectionId: '#equipment-specifications',
          name: 'vehicle-equipment',
          labelText: 'Equipment',
          hideReportItemsCount: true,
          visible: true,
        },
      ],
    },
    {
      labelText: 'Safety & Reliability',
      name: 'safety-reliability',
      icons: [
        <InfoOutlined
          sx={{
            color: theme?.palette?.primary?.main || '#0052FF',
            fontSize: '1.4rem',
          }}
        />,
      ],
      visible: true,
      childItems: [
        {
          href: '#vehicle-safety-ratings',
          sectionId: '#vehicle-safety-ratings',
          name: 'vehicle-safety-ratings',
          labelText: 'Safety Ratings',
          hideReportItemsCount: true,
          visible: true,
          notificationSection: 'safety',
        },
        {
          href: '#vehicle-warranty',
          sectionId: '#vehicle-warranty',
          name: 'vehicle-warranty',
          labelText: 'Warranties',
          hideReportItemsCount: true,
          visible: true,
          notificationSection: 'warranties',
        },
        {
          href: '#vehicle-safety-complaints-section',
          sectionId: '#vehicle-safety-complaints-section',
          name: 'vehicle-safety-complaints-section',
          labelText: 'Complaints',
          hideReportItemsCount: true,
          visible: true,
          notificationSection: 'complaints',
        },
      ],
    },
    {
      labelText: 'Community Buzz',
      name: 'community-buzz',
      href: '#community-buzz-section',
      sectionId: '#community-buzz-section',
      hideReportItemsCount: true,
      icons: [
        <Groups
          key="community-buzz-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
    },
    {
      labelText: 'Report attachments',
      name: 'attachments',
      href: '#attachments-section',
      sectionId: '#attachments-section',
      hideReportItemsCount: true,
      icons: [
        <Attachment
          key="attachments-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
    },
  ];

  return navLinkData
    .map((item) => ({
      ...item,
      childItems: item.childItems
        ? item.childItems.filter((child) => child.visible)
        : [],
    }))
    .filter((item) => item.visible || item.childItems.length > 0);
};

export const getVehicleNavLinkData = (
  reportData: SerializedVehicleReport | null,
  complaintsCount: number
) => {
  const automobile = reportData?.automobiles[0];

  if (!reportData) {
    return [];
  }

  const isYMMSearch = reportData.meta?.search_by === 'ymm';

  const safetyCount =
    reportData?.automobiles[0]?.safety_ratings?.length + complaintsCount;
  const ymmPresent = [
    automobile?.year,
    automobile?.make,
    automobile?.model,
  ].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const shouldShowSmartInsightsNavLink = !isYMMSearch && hasAllYMMPresent;

  const navLinkData: NavLinkData[] = [
    {
      href: '#vehicle-report-overview',
      sectionId: '#vehicle-overview',
      text: 'Photos',
      reportItemsCount: automobile?.imagesCount || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'identity',
      name: 'vehicle-photos',
    },
    {
      href: '#smart-insights',
      sectionId: '#smart-insights',
      text: 'Smart Insights',
      visible: shouldShowSmartInsightsNavLink,
      reportItemsCount: 0,
      notificationSection: 'smart insights',
      icon: <AIPill />,
      showIcon: true,
      name: 'smart-insights',
    },
    {
      href: '#detailed-history',
      sectionId: '#detailed-history',
      text: 'Detailed History',
      reportItemsCount: automobile?.detailedHistoryCount || null,
      hideReportItemsCount: false,
      visible: !!(!isYMMSearch && automobile?.detailedHistoryCount),
      notificationSection: 'history',
      name: 'detailed-history',
    },
    {
      href: '#vehicle-inspections',
      sectionId: '#vehicle-inspections',
      name: 'vehicle-inspections',
      text: 'Inspections',
      reportItemsCount: automobile?.inspectionEventsCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
    },
    {
      href: '#vehicle-sales',
      sectionId: '#vehicle-sales',
      text: 'Sale Listings',
      reportItemsCount: automobile?.listingsCount || null,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      notificationSection: 'sales',
      name: 'vehicle-sale-listings',
    },
    {
      href: '#vehicle-damage-history',
      sectionId: '#vehicle-damage-history',
      text: 'Damage History',
      reportItemsCount: Number(automobile?.damageReportedCount) || null,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      name: 'vehicle-damage-history',
      icon: <Lock fontSize="small" sx={{ marginRight: '-6px' }} />,
      showIcon:
        !automobile?.nmvtis_success &&
        Number(automobile?.damageReportedCount) === 0,
    },
    {
      href: '#vehicle-thefts',
      sectionId: '#vehicle-thefts',
      text: 'Theft Records',
      reportItemsCount: automobile?.theftsCount || null,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      notificationSection: 'thefts',
      name: 'vehicle-theft-records',
    },
    {
      href: 'vehicle-inspections-section',
      sectionId: 'vehicle-inspections-section',
      name: 'vehicle-inspections',
      text: 'Inspection Records',
      reportItemsCount: automobile?.inspectionEventsCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
    },
    {
      href: '#vehicle-service-and-repairs',
      sectionId: '#vehicle-service-and-repairs',
      text: 'Maintenance',
      reportItemsCount: automobile?.servicesAndRepairsCount || null,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      notificationSection: 'maintenance',
      name: 'maintenance',
    },
    {
      href: '#vehicle-ownership-history',
      sectionId: 'vehicle-ownership-history',
      text: 'Ownership History',
      reportItemsCount: Math.max(
        (automobile?.ownersByTitleRecordsCount as number) || 0,
        automobile?.ownershipHistoryCount || 0
      ),
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      name: 'vehicle-ownership-history',
      icon: <Lock fontSize="small" sx={{ marginRight: '-6px' }} />,
      showIcon:
        !automobile?.nmvtis_success && automobile?.titleRecordsCount === 0,
    },
    {
      href: '#vehicle-recalls',
      sectionId: '#vehicle-recalls',
      text: 'Recalls',
      reportItemsCount:
        (isYMMSearch
          ? automobile?.recallsCount
          : automobile?.recallsWithStatusCount) || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'prices',
      name: 'vehicle-recalls',
    },
    {
      href: '#vehicle-market',
      sectionId: '#vehicle-market',
      text: 'Market Value',
      reportItemsCount: automobile?.marketValueCount || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'market',
      name: 'vehicle-market',
    },
    {
      href: '#vehicle-ownership-cost',
      sectionId: '#vehicle-ownership-cost',
      text: 'Ownership Cost',
      reportItemsCount: automobile?.costsCount || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'costs',
      name: 'vehicle-ownership-cost',
    },
    {
      href: '#vehicle-specs',
      sectionId: '#vehicle-specs',
      text: 'Vehicle Specs',
      reportItemsCount: automobile?.specsCount || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'specifications',
      name: 'vehicle-specs',
    },
    {
      href: '#vehicle-warranty',
      sectionId: '#vehicle-warranty',
      text: 'Warranties',
      reportItemsCount: automobile?.warrantiesCount || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'warranties',
      name: 'vehicle-warranties',
    },
    {
      href: '#vehicle-safety-ratings',
      sectionId: '#vehicle-safety-ratings',
      text: 'Vehicle Safety',
      reportItemsCount: safetyCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'safety',
      name: 'vehicle-safety-ratings',
    },
  ];

  const moveNavLink = (
    navLinkArray: NavLinkData[],
    sectionIdToMove: string,
    sectionIdAfter: string,
    GAEventLabel: string
  ) => {
    const currentIndex = navLinkArray.findIndex(
      (item) => item.sectionId === sectionIdToMove
    );

    const newIndex = navLinkArray.findIndex(
      (item) => item.sectionId === sectionIdAfter
    );

    if (currentIndex !== -1 && newIndex !== -1) {
      const [movedNavLink] = navLinkArray.splice(currentIndex, 1);
      (movedNavLink as NavLinkData).GAEventLabel = GAEventLabel;
      navLinkArray.splice(newIndex + 1, 0, movedNavLink);
    }
  };

  moveNavLink(
    navLinkData,
    'vehicle-ownership-history',
    '#vehicle-overview',
    'ownership history NEW'
  );
  moveNavLink(
    navLinkData,
    '#vehicle-specs',
    'vehicle-ownership-history',
    'vehicle specifications NEW'
  );

  if (shouldShowSmartInsightsNavLink) {
    moveNavLink(
      navLinkData,
      '#smart-insights',
      '#vehicle-overview',
      'smart insights NEW'
    );
  }

  return navLinkData.filter((item) => item.visible);
};
